import {
  formatGeoItemDialogTitle,
  formatGeoItemNumberRef,
  formatGeoItemOptionalHashId,
  isGeoItemNumericId,
} from '@civilgrid/utils/src/geoItems/formatGeoItemId';
import mime from 'mime';
import { getReasonForError } from './getReasonForError';
import { captureTypedPosthogEvent, type FileDownloadSource, POSTHOG_EVENTS } from './posthogEvents';
import type { CivilGridFile, FileCategory, FileDownload } from './types';

export { formatGeoItemDialogTitle, formatGeoItemNumberRef, formatGeoItemOptionalHashId, isGeoItemNumericId };

export const REGEX_PATTERN = /[\r\n\t\f\v]/g;

export function removeWhitespaceRegEx(original: string) {
  const text = original.replace(REGEX_PATTERN, '');
  return text;
}

/** Display text for a geo-item prop value (multi-select values sync from mobile as string arrays). */
export function geoItemPropValueText(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value.join(', ') : value;
}

// UTF-8 byte-order-mark. Without it, Excel guesses encoding from the system codepage
// instead of trusting `charset=utf-8`, mojibake-ing multi-byte characters. Invisible
// and safe — other tools recognize and skip it, or ignore it.
const UTF8_BOM = '﻿';

export function downloadCSV(csv: string, name: string) {
  const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();

  window.URL.revokeObjectURL(url);
}

interface ConstructFileOptions {
  notes?: string;
  category?: FileCategory;
}

export function constructFile(file: File, options?: ConstructFileOptions) {
  const f = new FormData();
  // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: TODO
  const ext = file.name?.split('.').pop()!;

  f.append('file', file);
  f.append('name', file.name);
  if (file.type) {
    // if the file has an existing mime type, use it
    f.append('type', file.type);
  } else {
    // if not, generate one
    const mimeType = mime.getType(ext);
    if (mimeType) {
      f.append('type', mimeType);
    }
  }
  f.append('extension', ext);

  if (options?.notes) {
    f.append('notes', options.notes);
  }

  if (options?.category) {
    f.append('category', options.category);
  }

  return f;
}

/**
 * Hands a stored file to the browser's download manager through a signed attachment URL; records a
 * failure to get the URL and rethrows it so the caller can tell the user.
 */
export async function streamFile(
  getDownload: () => Promise<FileDownload>,
  fileName: string,
  source: FileDownloadSource,
): Promise<FileDownload> {
  let download: FileDownload;
  try {
    download = await getDownload();
  } catch (err) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.fileDownloadFailed, {
      source,
      file_name: fileName,
      reason: getReasonForError(err),
    });
    throw err;
  }

  // The URL is signed as an attachment, so navigating to it saves the file and leaves the page in place.
  window.location.href = download.url;
  return download;
}

export const isValidEmail = (text: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return emailRegex.test(text);
};
